import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { usePersonnel } from "../../../hooks/usePersonnel";

interface PlayerProfileModalProps {
  open: boolean;
  onClose: () => void;
  player: any; // Replace with proper Player type
  membership: any; // Replace with proper RosterMembership type
  rosterId: number | null;
  onUpdate: () => void;
  onRemove: (rosterId: number, playerId: number) => Promise<void>;
}

export const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({
  open,
  onClose,
  player,
  membership,
  rosterId,
  onUpdate,
  onRemove
}) => {
  const personnelController = usePersonnel();
  
  // Define position options
  const offensivePositions = ['OL', 'RB', 'TE', 'QB', 'WR'];
  const defensivePositions = ['DL', 'LB', 'DB'];
  
  const [formData, setFormData] = useState({
    // Player data
    name: "",
    offensivePosition: "",
    defensivePosition: "",
    positionGroup: "",
    height: "",
    weight: "",
    gradYear: "",
    // Personal information fields
    email: "",
    phone: "",
    gpa: "",
    city: "",
    state: "",
    country: "",

    // Membership data
    jerseyNumber: "",
    status: "ACTIVE",
  });

  // Reset form data when player changes
  useEffect(() => {
    if (player && membership) {
      console.log("Player data received:", player);
      console.log("Membership data received:", membership);

      setFormData({
        name: player.name || "",
        offensivePosition: player.offensive_position || "",
        defensivePosition: player.defensive_position || "",
        positionGroup: player.position_group || "",
        height: player.height || "",
        weight: player.weight || "",
        gradYear: player.grad_year || "",
        // Set personal information fields
        email: player.email || "",
        phone: player.phone || "",
        gpa: player.gpa || "",
        city: player.city || "",
        state: player.state || "",
        country: player.country || "",
        jerseyNumber: membership.jersey_number || "",
        status: membership.status || "ACTIVE",
      });
    }
  }, [player, membership]);

  // Format height from inches to feet and inches
  const formatHeight = (inches: number) => {
    if (!inches) return "";
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };

  // Handle input change for TextField components
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle select change for Select components
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      name: player.name || "",
      offensivePosition: player.offensive_position || "",
      defensivePosition: player.defensive_position || "",
      positionGroup: player.position_group || "",
      height: player.height || "",
      weight: player.weight || "",
      gradYear: player.grad_year || "",
      email: player.email || "",
      phone: player.phone || "",
      gpa: player.gpa || "",
      city: player.city || "",
      state: player.state || "",
      country: player.country || "",
      jerseyNumber: membership.jersey_number || "",
      status: membership.status || "ACTIVE",
    });
    onClose();
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.name || !formData.gradYear) {
        console.error("Name and graduation year are required");
        return;
      }

      // Validate graduation year
      if (!formData.gradYear) {
        // You might want to add some error state management here
        console.error("Graduation year is required");
        return;
      }

      // Prepare player data for update or creation
      const playerData: any = {
        name: formData.name,
        offensive_position: formData.offensivePosition,
        defensive_position: formData.defensivePosition,
        position_group: formData.positionGroup,
        height: Number(formData.height) || null,
        weight: Number(formData.weight) || null,
        grad_year: formData.gradYear,
        email: formData.email,
        phone: formData.phone,
        gpa: formData.gpa ? Number(formData.gpa) : null,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        // Add jersey number and status for membership
        jerseyNumber: Number(formData.jerseyNumber) || null,
        status: formData.status
      };

      console.log("Submitting player data:", playerData);

      // If we're editing an existing player, add their ID
      if (player && player.id) {
        playerData.id = player.id;
      }

      // If we're editing an existing membership, add its ID
      if (membership && membership.id) {
        playerData.membershipId = membership.id;
      }

      // Log the roster ID to make sure it's available
      console.log("Saving player to roster ID:", rosterId);

      // Create or update the player and their roster membership
      const result = await personnelController.createOrUpdatePlayer(playerData, rosterId);

      if (!result) {
        throw new Error("Failed to create or update player");
      }

      onUpdate();
      onClose();
    } catch (error: any) {
      console.error("Failed to save player:", error);
      console.error("Error details:", error.response?.data || error.message);
      // Handle error (could add error state and display error message)
    }
  };

  // Get status color based on player status
  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INJURED":
        return "error";
      case "REDSHIRT":
        return "warning";
      case "PRACTICE":
        return "info";
      case "INACTIVE":
        return "default";
      default:
        return "default";
    }
  };

  // Generate graduation year options (current year to current year + 8)
  const currentYear = new Date().getFullYear();
  const gradYearOptions = Array.from({ length: 9 }, (_, i) =>
    (currentYear + i).toString()
  );

  // For the display name
  const playerDisplayName = player?.name || "Add Player";

  // Add state for confirmation dialog
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  // Handle showing the confirmation dialog
  const handleRemoveClick = () => {
    setRemoveDialogOpen(true);
  };

  // Handle the actual removal after confirmation
  const handleRemoveConfirm = async () => {
    if (player?.id && rosterId) {
      await onRemove(rosterId, player.id);
      setRemoveDialogOpen(false);
      onClose();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="player-profile-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1200,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          
          {/* Header with name and status */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ mr: 2 }}>
              {playerDisplayName}
            </Typography>
            {membership?.status && (
              <Chip
                label={membership.status}
                color={getStatusColor(membership.status) as any}
                size="medium"
              />
            )}
          </Box>
          
          <Grid container spacing={2}>
            {/* PLAYER INFORMATION SECTION */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold">Player Information</Typography>
            </Grid>
            
            {/* Name, Jersey Number, Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name *"
                fullWidth
                variant="outlined"
                size="small"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                error={!formData.name}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                label="Jersey Number"
                fullWidth
                variant="outlined"
                size="small"
                name="jerseyNumber"
                type="number"
                value={formData.jerseyNumber}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INJURED">Injured</MenuItem>
                  <MenuItem value="REDSHIRT">Redshirt</MenuItem>
                  <MenuItem value="PRACTICE">Practice</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Physical Information Row */}
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <TextField
                  label="Height (inches)"
                  variant="outlined"
                  size="small"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={handleInputChange}
                  sx={{ width: '60%' }}
                />
                <Box 
                  sx={{ 
                    width: '40%', 
                    height: '40px', 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: 1,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: theme => theme.palette.text.primary
                  }}
                >
                  {formData.height ? formatHeight(Number(formData.height)) : "—"}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                label="Weight (lbs)"
                fullWidth
                variant="outlined"
                size="small"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Graduation Year</InputLabel>
                <Select
                  name="gradYear"
                  value={formData.gradYear}
                  label="Graduation Year *"
                  onChange={handleSelectChange}
                  error={!formData.gradYear}
                >
                  <MenuItem value="">Select Year</MenuItem>
                  {gradYearOptions.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                label="GPA"
                fullWidth
                variant="outlined"
                size="small"
                name="gpa"
                type="number"
                inputProps={{ min: 0, max: 4, step: 0.1 }}
                value={formData.gpa}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Position Information Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Position Information</Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Offensive Position</InputLabel>
                <Select
                  name="offensivePosition"
                  value={formData.offensivePosition}
                  label="Offensive Position"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {offensivePositions.map((position) => (
                    <MenuItem key={position} value={position}>{position}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Defensive Position</InputLabel>
                <Select
                  name="defensivePosition"
                  value={formData.defensivePosition}
                  label="Defensive Position"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {defensivePositions.map((position) => (
                    <MenuItem key={position} value={position}>{position}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Position Group</InputLabel>
                <Select
                  name="positionGroup"
                  value={formData.positionGroup}
                  label="Position Group"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="SKILL">Skill</MenuItem>
                  <MenuItem value="BIG_SKILL">Big Skill</MenuItem>
                  <MenuItem value="BIG">Big</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Personal Information Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Personal Information</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                variant="outlined"
                size="small"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                variant="outlined"
                size="small"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                fullWidth
                variant="outlined"
                size="small"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="State/Province"
                fullWidth
                variant="outlined"
                size="small"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Country"
                fullWidth
                variant="outlined"
                size="small"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Action Buttons */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              {player?.id && (
                <Button 
                  variant="outlined" 
                  color="error"
                  onClick={handleRemoveClick}
                >
                  Remove Player from Roster
                </Button>
              )}
              <Button 
                variant="outlined" 
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* Add the confirmation dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
      >
        <DialogTitle>Remove Player</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {player?.name} from the roster?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
